import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Clock, Globe, Loader2, Phone, Save, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { GbpHoursEditor } from "@/components/gbp/GbpHoursEditor";

const MAX_DESC = 750;
const sortHours = (hs) => [...hs].map((h) => `${h.day}|${h.open}|${h.close}`).sort().join(",");
const FIELD_LABEL = { profile: "description", websiteUri: "site web", phoneNumbers: "téléphones", regularHours: "horaires" };

export function GbpProfilePanel({ profile, locationId, siteId, onSaved }) {
  const [form, setForm] = useState(profile);
  const [saving, setSaving] = useState(false);
  const [variants, setVariants] = useState(null);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => { setForm(profile); setVariants(null); }, [profile]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const changed = {
    description: form.description !== profile.description,
    website_uri: form.website_uri !== profile.website_uri,
    phones: form.primary_phone !== profile.primary_phone || form.additional_phones.join("|") !== profile.additional_phones.join("|"),
    regular_hours: sortHours(form.regular_hours) !== sortHours(profile.regular_hours),
  };
  const dirty = Object.values(changed).some(Boolean);
  const descTooLong = form.description.length > MAX_DESC;
  const hoursCleared = form.regular_hours.length === 0 && profile.regular_hours.length > 0;

  const save = async () => {
    const body = {};
    if (changed.description) body.description = form.description;
    if (changed.website_uri) body.website_uri = form.website_uri;
    if (changed.phones) { body.primary_phone = form.primary_phone; body.additional_phones = form.additional_phones.filter((p) => p.trim()); }
    if (changed.regular_hours) body.regular_hours = form.regular_hours;
    setSaving(true);
    try {
      const { data } = await api.put(`/gbp/locations/${locationId}/profile`, body);
      toast.success(data.updated?.length
        ? `Fiche mise à jour sur Google : ${data.updated.map((f) => FIELD_LABEL[f] || f).join(", ")}.`
        : "Aucun changement à envoyer.");
      onSaved?.(data.profile);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mise à jour refusée par Google.");
    } finally {
      setSaving(false);
    }
  };

  const suggest = async () => {
    if (!siteId) return toast.error("Sélectionnez un site actif.");
    setLoadingVariants(true);
    try {
      const { data } = await api.post(`/sites/${siteId}/gbp-description-suggestions`, { location_id: locationId });
      toast.info("L'IA rédige 3 variantes de description (15-30 s)…");
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts += 1;
        if (attempts > 40) { clearInterval(pollRef.current); setLoadingVariants(false); toast.error("Génération trop longue. Réessayez."); return; }
        try {
          const { data: job } = await api.get(`/content/jobs/${data.job_id}`);
          if (job.status === "completed") { clearInterval(pollRef.current); setLoadingVariants(false); setVariants(job.result?.variants || []); }
          else if (job.status === "failed") { clearInterval(pollRef.current); setLoadingVariants(false); toast.error(job.error || "Génération échouée."); }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (err) {
      setLoadingVariants(false);
      toast.error(err?.response?.data?.detail || "Impossible de lancer la génération.");
    }
  };

  const setPhone = (i, value) => {
    const next = [...form.additional_phones];
    next[i] = value;
    set({ additional_phones: next.slice(0, 2) });
  };

  return (
    <div className="border border-slate-200 bg-white rounded-lg p-5 mb-6" data-testid="gbp-profile-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="text-sm font-semibold text-slate-900">
          Informations de la fiche
          {dirty && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="gbp-profile-dirty">Non enregistré</span>}
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" disabled={!dirty || saving || descTooLong || hoursCleared} data-testid="gbp-profile-save-button" className="bg-[#002FA7] hover:bg-[#00248A] text-white">
              {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Envoi…</> : <><Save className="w-3.5 h-3.5 mr-1.5" /> Enregistrer sur Google</>}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mettre à jour la fiche Google ?</AlertDialogTitle>
              <AlertDialogDescription>
                Champs envoyés : {[changed.description && "description", changed.website_uri && "site web", changed.phones && "téléphones", changed.regular_hours && "horaires"].filter(Boolean).join(", ")}.
                Google peut soumettre certaines modifications à validation avant affichage public.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="gbp-profile-save-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={save} data-testid="gbp-profile-save-confirm" className="bg-[#002FA7] hover:bg-[#00248A]">Publier sur Google</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-slate-700">Description « À propos »</label>
              <Button variant="outline" size="sm" onClick={suggest} disabled={loadingVariants} data-testid="gbp-desc-suggest-button" className="h-7 text-xs border-[#002FA7]/40 text-[#002FA7]">
                {loadingVariants ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> IA…</> : <><Sparkles className="w-3 h-3 mr-1" /> 3 variantes IA</>}
              </Button>
            </div>
            <textarea
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
              rows={7}
              data-testid="gbp-desc-input"
              placeholder="Présentez votre établissement : activité, zone desservie, ce qui vous distingue…"
              className={`w-full text-sm text-slate-800 border rounded-md px-3 py-2 focus:outline-none focus:border-[#002FA7] ${descTooLong ? "border-red-400" : "border-slate-200"}`}
            />
            <div className={`text-[11px] text-right ${descTooLong ? "text-red-600 font-semibold" : "text-slate-400"}`} data-testid="gbp-desc-counter">
              {form.description.length}/{MAX_DESC}
            </div>
            {variants && (
              <div className="mt-2 grid gap-2" data-testid="gbp-desc-variants">
                {variants.map((v, i) => {
                  const active = form.description === v.text;
                  return (
                    <div key={v.style} className={`border rounded-md p-3 ${active ? "border-[#002FA7] bg-blue-50/40" : "border-slate-200"}`} data-testid={`gbp-desc-variant-${i}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-bold uppercase tracking-wider text-[#002FA7]">{v.label}</span>
                        <span className="text-[11px] text-slate-400">{v.chars ?? v.text.length} car.</span>
                      </div>
                      <p className="text-xs text-slate-700 whitespace-pre-line">{v.text}</p>
                      <button onClick={() => set({ description: v.text })} disabled={active} data-testid={`gbp-desc-variant-use-${i}`}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-medium rounded-md px-2 py-1 border border-[#002FA7]/40 text-[#002FA7] hover:bg-blue-50 disabled:opacity-60">
                        {active ? <><Check className="w-3.5 h-3.5" /> Utilisée</> : "Utiliser ce texte"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-1"><Globe className="w-3.5 h-3.5" /> Site web</label>
              <input value={form.website_uri} onChange={(e) => set({ website_uri: e.target.value })} placeholder="https://www.exemple.ch" data-testid="gbp-website-input"
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-1.5 focus:outline-none focus:border-[#002FA7]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-1"><Phone className="w-3.5 h-3.5" /> Téléphone principal</label>
              <input value={form.primary_phone} onChange={(e) => set({ primary_phone: e.target.value })} placeholder="+41 22 123 45 67" data-testid="gbp-phone-input"
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-1.5 focus:outline-none focus:border-[#002FA7]" />
              {[0, 1].map((i) => (
                <input key={i} value={form.additional_phones[i] || ""} onChange={(e) => setPhone(i, e.target.value)} placeholder={`Téléphone supplémentaire ${i + 1} (optionnel)`} data-testid={`gbp-phone-extra-${i}`}
                  className="w-full text-xs border border-slate-200 rounded-md px-3 py-1 mt-1.5 focus:outline-none focus:border-[#002FA7]" />
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2"><Clock className="w-3.5 h-3.5" /> Horaires d'ouverture</label>
          <GbpHoursEditor hours={form.regular_hours} onChange={(hs) => set({ regular_hours: hs })} />
          {hoursCleared && (
            <p className="text-[11px] text-red-600 mt-2" data-testid="gbp-hours-cleared-hint">
              Gardez au moins une plage : la suppression complète des horaires se fait dans Google Business Profile.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
